import { EditIcon, TrashAltIcon, CrossCircleIcon } from "assets/icons";
import { GoBackHeaderTitle, If } from "components";
import { BoxShadowLayout, ContentLayout } from "layouts";
import { useRefetchQuery } from "libs/reactQuery";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import { Button } from "ui";

import {
  DELIVERY_COMPANIES_QUERY_KEY,
  useGetDeliveryCompany,
  useUpdateDeliveryCompany,
  useToggleDeliveryCompanyActive,
  useDeleteDeliveryCompany
} from "../../apis";
import {
  CompanyDetailsHeader,
  CompanyDetailsTabs,
  CompanyDetailsTab,
  CompanyFormModal
} from "../../components";
import { DeliveryCompanyFormValues } from "../../deliveryCompanies.types";
import { CompanyDriversTab, CompanyMetricsTab, CompanyOrdersTab } from "./tabs";

import styles from "./CompanyDetails.module.scss";

const isValidTab = (value: string): value is CompanyDetailsTab =>
  value === "metrics" || value === "orders" || value === "drivers";

const CompanyDetails = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId, activeTab } = useParams();

  const { company, isLoading } = useGetDeliveryCompany(companyId);
  const refetchCompanies = useRefetchQuery([DELIVERY_COMPANIES_QUERY_KEY]);

  const tab: CompanyDetailsTab =
    activeTab && isValidTab(activeTab) ? activeTab : "metrics";

  const [isEditOpen, setIsEditOpen] = useState(false);

  const updateMutation = useUpdateDeliveryCompany({
    onSuccess: async () => {
      toast.success(t("Company updated"));
      setIsEditOpen(false);
      await refetchCompanies();
    }
  });
  const toggleMutation = useToggleDeliveryCompanyActive({
    onSuccess: async () => {
      await refetchCompanies();
    }
  });
  const deleteMutation = useDeleteDeliveryCompany({
    onSuccess: async () => {
      toast.success(t("Company deleted"));
      await refetchCompanies();
      navigate("/companies");
    }
  });

  const handleEditSubmit = (values: DeliveryCompanyFormValues) => {
    if (!company) return;
    const capacity = typeof values.capacity === "number" ? values.capacity : 0;
    updateMutation.mutate({
      id: company.id,
      name: values.name,
      email: values.email,
      password: values.password ? values.password : undefined,
      capacity,
      phoneNumber: values.phoneNumber || undefined,
      address: values.address || undefined,
      avatarMediaId: values.avatarMediaId
    });
  };

  const handleToggleBlock = () => {
    if (!company) return;
    toggleMutation.mutate({ id: company.id, isActive: !company.isActive });
  };

  const handleDelete = () => {
    if (!company) return;
    if (window.confirm(t("Delete this company?"))) {
      deleteMutation.mutate(company.id);
    }
  };

  const isBlocked = !company?.isActive;

  return (
    <ContentLayout
      title={
        <GoBackHeaderTitle
          title={t("Company")}
          onGoBack={() => navigate("/companies")}
        />
      }
      action={
        company ? (
          <div className={styles.companyDetails__headerActions}>
            <Button
              variant="default"
              icon={<EditIcon width={18} height={18} fill="#090914" />}
              onClick={() => setIsEditOpen(true)}
            >
              {t("Edit")}
            </Button>
            <Button
              variant="default"
              icon={<CrossCircleIcon width={18} height={18} />}
              onClick={handleToggleBlock}
            >
              {isBlocked ? t("Unblock") : t("Block")}
            </Button>
            <Button
              variant="text-delete"
              icon={<TrashAltIcon width={18} height={18} fill="#FF0033" />}
              onClick={handleDelete}
            >
              {t("Delete")}
            </Button>
          </div>
        ) : null
      }
    >
      <If condition={isLoading}>
        <div className={styles.companyDetails__loader}>
          <ClipLoader size={30} color="#fb3973" />
        </div>
      </If>

      {!isLoading && company && (
        <BoxShadowLayout className={styles.companyDetails}>
          <CompanyDetailsHeader
            company={company}
            aggregates={{
              activeDriverCount: company.activeDriverCount,
              totalDriverCount: company.totalDriverCount,
              todayOrdersCount: company.todayOrdersCount,
              capacityUsed: company.capacityUsed
            }}
          />

          <CompanyDetailsTabs companyId={company.id} activeTab={tab} />

          {tab === "metrics" && <CompanyMetricsTab companyId={company.id} />}
          {tab === "orders" && <CompanyOrdersTab companyId={company.id} />}
          {tab === "drivers" && <CompanyDriversTab companyId={company.id} />}
        </BoxShadowLayout>
      )}

      <CompanyFormModal
        isOpen={isEditOpen}
        editableCompany={company ?? null}
        isSubmitting={updateMutation.isLoading}
        onClose={() => setIsEditOpen(false)}
        onSubmit={handleEditSubmit}
      />
    </ContentLayout>
  );
};

export default CompanyDetails;
